/*
 * Copyright (C) 2023 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import {
  BlobWithFileName,
  ContentType,
  HttpHeader,
  RequestOptions,
  SkipInterceptor,
  buildBlobWithFileName,
} from '@common/tech-shared';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Resource, ResourceFactory, ResourceUri, getUrl } from '@ngxp/rest';
import { Observable, map } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { BinaryFileLinkRel } from './binary-file.linkrel';
import { BinaryFileListResource, BinaryFileResource } from './binary-file.model';

@Injectable({ providedIn: 'root' })
export class BinaryFileRepository {
  private readonly httpClient = inject(HttpClient);
  private readonly resourceFactory = inject(ResourceFactory);

  public uploadFile(uri: ResourceUri, file: File): Observable<BinaryFileResource> {
    const formData: FormData = new FormData();

    formData.append('file', file, file.name);

    return this.httpClient
      .post(uri, formData, { observe: 'response' })
      .pipe(switchMap((response: HttpResponse<object>) => this.getFile(this.getLocation(response))));
  }

  private getLocation(response: HttpResponse<object>): ResourceUri {
    return response.headers.get(HttpHeader.LOCATION);
  }

  public download(fileResource: BinaryFileResource): Observable<Blob> {
    return this.doDownload(getUrl(fileResource, BinaryFileLinkRel.DOWNLOAD), this.buildRequestOptions());
  }

  buildRequestOptions(): RequestOptions {
    return this.buildBaseRequestOptions([ContentType.APPLICATION_ALL, ContentType.IMAGES_ALL]);
  }

  @SkipInterceptor()
  public downloadPdf(uri: ResourceUri): Observable<Blob> {
    return this.doDownload(uri, this.buildPdfRequestOptions());
  }

  private doDownload(uri: ResourceUri, requestOptions: RequestOptions): Observable<Blob> {
    return this.httpClient.get<Blob>(uri, requestOptions);
  }

  buildPdfRequestOptions(): RequestOptions {
    return this.buildBaseRequestOptions([ContentType.APPLICATION_PDF, ContentType.APPLICATION_JSON]);
  }

  buildBaseRequestOptions(contentTypes: ContentType[]): RequestOptions {
    let headers = new HttpHeaders();
    headers = headers.set(HttpHeader.ACCEPT, contentTypes);
    return { headers, responseType: 'blob' as 'json' };
  }

  public getFile(fileUri: ResourceUri): Observable<BinaryFileResource> {
    return this.resourceFactory.fromId(fileUri).get();
  }

  public getFiles(resource: Resource, linkRel: string): Observable<BinaryFileListResource> {
    return this.resourceFactory.from(resource).get(linkRel);
  }

  public downloadArchive(uri: ResourceUri): Observable<BlobWithFileName> {
    return this.httpClient.get<HttpResponse<Blob>>(uri, this.buildRequestOptionsForArchive()).pipe(map(buildBlobWithFileName));
  }

  buildRequestOptionsForArchive(): RequestOptions {
    let headers = new HttpHeaders();
    headers = headers.set(HttpHeader.ACCEPT, ContentType.APPLICATION_ZIP);
    return <RequestOptions>{ headers, responseType: 'blob' as 'json', observe: 'response' };
  }
}
